/*eslint func-names: ["error", "never"]*/
import axios from 'axios';
import store from '@/core/store/PersistentStorage';
import authService from "@/core/services/AuthenticationService";

const RETRY_HEADER = 'X-Retry-After-Refresh';

const bookshelfApiClient: any = axios.create({
    baseURL: (import.meta.env.VITE_SERVERLESS == true) ? '/mock_api' : (import.meta.env.VITE_BOOKSHELF_API_HOST + '/api')
});

bookshelfApiClient.allowedCodes = [];

bookshelfApiClient.token = null;

if (import.meta.env.VITE_SERVERLESS == true) {
    bookshelfApiClient.post = function (a) {
        return bookshelfApiClient.get(a);
    };
}

/* When chained into a request, this function allows a specific HTTP response code, or array of codes, without throwing the "A system error occurred" notification in the UI.
   Use like this: apiClient.allow([422, 421]).post(...)

   This is useful if you want specific server errors to be handled within your component, such as for form validation.
 */
bookshelfApiClient.allow = function (errorCodes) {
    if (Array.isArray(errorCodes)) {
        bookshelfApiClient.allowedCodes = errorCodes;
    } else {
        bookshelfApiClient.allowedCodes = [errorCodes];
    }
    return this;
};

bookshelfApiClient.download = function (url) {
    return bookshelfApiClient({url, method: 'GET', responseType: 'blob'});
};

bookshelfApiClient.create = function (endpoint, data) {
    return this.post(endpoint, data);
};

bookshelfApiClient.update = function (endpoint, data) {
    return this.put(endpoint + '/' + data.id, data);
};

bookshelfApiClient.remove = function (endpoint, data) {
    return this.delete(endpoint + '/' + data.id);
};

bookshelfApiClient.interceptors.request.use(async (request) => {
    // validateSession may throw TransientRefreshError; let it propagate so the
    // request fails without ending the session. The caller's next action will retry.
    await authService.validateSession();
    if (store.authentication.accessToken) {
        request.headers.Authorization = `Bearer ${store.authentication.accessToken}`;
    }
    return request;
});

bookshelfApiClient.interceptors.response.use(
    response => response,
    async error => {
        const status = error.response?.status;
        const originalRequest = error.config;
        if (
            status !== 401 ||
            !originalRequest ||
            originalRequest.headers?.[RETRY_HEADER]
        ) {
            throw error;
        }
        let token: string | false;
        try {
            token = await authService.validateSession();
        } catch {
            throw error;
        }
        if (!token) throw error;
        originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${token}`,
            [RETRY_HEADER]: '1',
        };
        return bookshelfApiClient(originalRequest);
    }
);

export default bookshelfApiClient;
